import { Dispatch, PropsWithChildren, SetStateAction, useEffect } from 'react';
import Link from 'next/link';
import { ArrowUpLeft, SearchIcon } from 'lucide-react';
import Loading from '@/components/Loading/Loading';
import { useSearchSuggestions } from '@/lib/useSearchSuggestions';
import type { SearchProductsResponseBody } from '@/api/catalog-v2/search/products';
import { trackProductsSearched } from '@/lib/analytics/trackProduct';
import i18n from '@/settings/i18n';
import PopularSearches from './PopularSearches';

const t = i18n.init('shared');

export default function SearchOutputMobile({
  children,
  search,
  toggle,
  setResults,
  onClose,
  onAutofill,
  onSearchNavigate,
}: PropsWithChildren<{
  search: string;
  toggle?: () => void;
  setResults: Dispatch<SetStateAction<SearchProductsResponseBody | null>>;
  onClose: () => void;
  onAutofill: (value: string) => void;
  onSearchNavigate?: () => void;
}>) {
  const { data, error } = useSearchSuggestions(search ? { q: search } : null);

  const isLoading = !data && !error && search.length > 0;
  const items = data?.items ?? [];
  const trimmedSearch = search.trim();

  useEffect(() => {
    if (data != null) {
      setResults(data);
    }
  }, [data, setResults]);

  const handleSelect = (track = false) => {
    if (track && trimmedSearch !== '') {
      trackProductsSearched(search);
    }

    onSearchNavigate?.();
    toggle?.();
    onClose();
  };

  return (
    <div className="relative w-full">
      {children}
      <div className="mt-4">
        {isLoading && (
          <div className="flex items-center justify-center py-6">
            <Loading fill="none" />
          </div>
        )}

        {!isLoading && trimmedSearch !== '' && (
          <section>
            <p className="text-sm font-bold leading-normal text-gray-700">
              {t`Suggestions`}
            </p>
            <ul className="mt-1">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center border-b border-gray-200 hover:bg-gray-200"
                >
                  <Link
                    href={`/collections/${item.slug}`}
                    onClick={() => handleSelect(true)}
                    className="flex min-w-0 flex-1 cursor-pointer items-center py-3"
                  >
                    <span className="truncate text-sm font-medium leading-normal">
                      {item.name}
                    </span>
                  </Link>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onAutofill(item.name);
                    }}
                    aria-label={`Fill search with ${item.name}`}
                    className="flex shrink-0 items-center justify-center p-2 text-gray-700"
                  >
                    <ArrowUpLeft className="h-4 w-4" />
                  </button>
                </li>
              ))}

              <li className="hover:bg-gray-200">
                <Link
                  href={`/search?query=${encodeURIComponent(search)}`}
                  onClick={() => handleSelect(true)}
                  className="flex cursor-pointer items-center justify-between py-3"
                >
                  <span className="truncate text-sm font-medium leading-normal">
                    &ldquo;{search}&rdquo;
                  </span>
                  <SearchIcon className="h-4 w-4 shrink-0 text-gray-700" />
                </Link>
              </li>
            </ul>
          </section>
        )}

        {!isLoading && (
          <div className={trimmedSearch !== '' ? 'mt-4' : undefined}>
            <PopularSearches variant="mobile" onSelect={() => handleSelect()} />
          </div>
        )}
      </div>
    </div>
  );
}
